"use client";

import React, { useEffect, useState, ReactNode } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import Image from "next/image";
import { Images } from "@assets/assets";

// Lazy-load the Swiper-based sliders: they (plus swiper + react-device-detect)
// only download when the popup actually opens, keeping them out of the initial
// bundle that hydrates on page load (big mobile TBT win).
const TestimonialSlider = dynamic(() => import("./Testimonial"), {
  ssr: false,
});
const TestimonialLight = dynamic(() => import("./TestimonialLight"), {
  ssr: false,
});

interface DesktopPopupProps {
  active: boolean;
  children: ReactNode;
  variant?: "nav" | "quote";
}

export default function DesktopPopup({
  active,
  children,
  variant = "nav",
}: DesktopPopupProps) {
  const [mounted, setMounted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const bodyClass = variant === "quote" ? "quote-popup-open" : "mega-menu-open";
  const isQuote = variant === "quote";

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    if (active) {
      setShouldRender(true);
      // Small delay to ensure the browser captures the starting position for the transition
      timeoutId = setTimeout(() => {
        setIsAnimating(true);
      }, 20);
      document.body.classList.add("overflow-hidden", bodyClass);
    } else {
      setIsAnimating(false);
      // Wait for 500ms transition to finish before unmounting
      timeoutId = setTimeout(() => {
        setShouldRender(false);
        document.body.classList.remove("overflow-hidden", bodyClass);
      }, 500);
    }

    return () => clearTimeout(timeoutId);
  }, [active, mounted, bodyClass]);

  // The body classes above are added imperatively and only removed by the
  // close branch's 500ms timeout. Desktop mega-menu links navigate WITHOUT
  // closing the menu, so on a route change this component unmounts while
  // `active` is still true: the timeout is cleared and `mega-menu-open` /
  // `overflow-hidden` stay on <body> forever. The next page then renders with
  // `body.mega-menu-open .hero-content { visibility: hidden }` (globals.css)
  // applied — i.e. a blank hero — and a locked scroll. Always unwind on unmount.
  useEffect(() => {
    return () => {
      document.body.classList.remove("overflow-hidden", bodyClass);
    };
  }, [bodyClass]);

  if (!mounted || !shouldRender) return null;

  return createPortal(
    <div
      className={`fixed inset-0 flex z-[999999] transition-opacity duration-500 
        ${isAnimating ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      style={{ isolation: "isolate" }}
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />

      {/* Left Side: Sliding from TOP */}
      <div
        className={`side-nav-area hidden relative z-10 h-full lg:flex flex-col justify-between transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] transform
          ${isAnimating ? "translate-y-0" : "-translate-y-full"}
          2xl:flex flex-grow-[362px] w-[362px] min-w-[362px] max-w-[362px] 
          ${isQuote ? "bg-[#ECF0F6]" : "bg-[#00329B]"}`}
      >
        <div className="flex flex-col h-full justify-between">
          <Image
            src={isQuote ? Images?.logoIcon : Images?.konstantLogo}
            alt="logo"
            width={60}
            height={60}
            className="size-[60px] ml-[80px] mt-[40px] mega-menu-logo"
            priority
            sizes="(max-width: 1024px) 100vw, 50vw"
            fetchPriority="high"
          />

          <div className="testimonials flex flex-col gap-8 pl-[80px] pt-8 [&_.swiper-pagination]:transform [&_.swiper-pagination]:translate-y-[-20px] [&_.swiper-pagination]:translate-x-[calc(-50%_+_28px)]">
            <div className="flex gap-2 items-center">
              <Image
                src={Images?.goldVerified}
                alt="verified"
                width={40}
                height={40}
                className={`size-[40px] ${!isQuote ? "filter grayscale brightness-[100]" : ""}`}
                priority
                sizes="(max-width: 1024px) 100vw, 50vw"
                fetchPriority="high"
              />
              <div
                className={`text-[14px] ${isQuote ? "text-[#636364]" : "text-white"} font-[400] flex flex-col gap-1`}
              >
                <Image
                  src={Images?.stars}
                  alt="stars"
                  width={60}
                  height={24}
                  className={`w-[60px] h-auto ${!isQuote ? "filter grayscale brightness-[100]" : ""}`}
                  priority
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  fetchPriority="high"
                />
                <h5 className={`${isQuote ? "text-[#000]" : "text-white"}`}>
                  Gold verified, 166 reviews
                </h5>
              </div>
            </div>
            {isQuote ? <TestimonialLight /> : <TestimonialSlider />}
          </div>
        </div>
      </div>

      {/* Right Side: Sliding from BOTTOM */}
      <div
        className={`flex-1 right-nav-area relative z-10 w-full flex flex-col justify-start gap-[60px] overflow-y-auto overflow-x-hidden max-h-screen transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] transform
          ${isAnimating ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}
          ${isQuote ? "bg-white" : "bg-[#225BD4]"}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
